import ChartDataLabels from "chartjs-plugin-datalabels"
import { Pie } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js"
import { Box, Typography } from "@material-ui/core"
import { keys } from "lodash"
import { getOrCreateTooltip } from "../../utils/tooltip"
import LegendItem from "../LegendItem"
import { useTranslations } from "./useTranslations"
import { GroupedProjectsBasedOnResources } from "../../api/projects-dashboard"
import { getStatusColorMap } from "./utils"
import * as pattern from "patternomaly"
import { useIsSmallScreen } from "../../../hooks/use-is-small-screen"

ChartJS.register(ArcElement, Tooltip, ChartDataLabels)

const useManagersBasedPieChart = (props: ManagersBasedPieChartProps) => {
  const translations = useTranslations()
  const { totalNumberOfProjects, groupedProjects } = props

  const managerIds = keys(groupedProjects)
  const colorVariationsMap = getStatusColorMap()
  const patternMap = {
    "1": "line",
    "2": "diagonal",
    "3": "square",
    "4": "zigzag",
  }

  const legendData = managerIds.map((managerId, index) => {
    const { numberOfProjects: value, percentageOfProjects, projects, name } = groupedProjects[managerId]
    const colorKey = index % 16
    const patternKey = Math.floor(index / 16)

    const label = name
    const boxColor = colorVariationsMap[colorKey.toString()]
    const percentage = percentageOfProjects?.toFixed(1)
    const data = { label, boxColor, value, percentage, projects, pattern: "" }
    if (patternKey) {
      data.pattern = patternKey > 4 ? patternMap["4"] : patternMap[patternKey.toString() as "1" | "2" | "3" | "4"]
    }
    return data
  })

  const data = {
    labels: legendData.map((item) => item.label),
    datasets: [
      {
        label: "Projects",
        data: legendData.map((item) => item.percentage),
        backgroundColor: legendData.map((item) => {
          if (item.pattern) return pattern.draw(item.pattern as unknown as any, item.boxColor)
          return item.boxColor
        }),
        borderColor: legendData.map((item) => item.boxColor),
        borderWidth: 1,
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
        external: function (context: any) {
          const { chart, tooltip } = context
          let tooltipEl = getOrCreateTooltip(chart)

          // Hide if no tooltip
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0"
            return
          }

          const dataIndex = tooltip.dataPoints[0]?.dataIndex
          if (dataIndex !== undefined) {
            const { label, percentage, value, projects } = legendData[dataIndex]
            let innerHtml = `<div style="font-weight: bold; margin-bottom: 4px;">${label}: ${percentage}% (${value})</div>`
            innerHtml += "<div>"
            projects.forEach((task: string) => {
              innerHtml += `<div>• ${task}</div>`
            })
            innerHtml += "</div>"

            tooltipEl.innerHTML = innerHtml
          }

          // Position the tooltip
          const position = context.chart.canvas.getBoundingClientRect()
          tooltipEl.style.opacity = "1"
          tooltipEl.style.left = position.left + window.pageXOffset + tooltip.caretX + "px"
          tooltipEl.style.top = position.top + window.pageYOffset + tooltip.caretY + "px"
        },
      },
      datalabels: {
        formatter: (percentage: number) => {
          const value = Math.round((percentage / 100) * totalNumberOfProjects)
          return percentage > 5 ? `${percentage}%\n${value}` : ""
        },
        color: "#fff",
        font: {
          size: 14,
          weight: "bold" as const,
        },
        anchor: "center" as const,
        align: "end" as const,
      },
    },
  }

  const displayPieChart = managerIds.length > 0
  return { data, options, displayPieChart, legendData: legendData, translations }
}

const ManagersBasedPieChart = (props: ManagersBasedPieChartProps) => {
  const isSmallScreen = useIsSmallScreen("xs")
  const { data, options, displayPieChart, legendData, translations } = useManagersBasedPieChart(props)

  if (!displayPieChart) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.noData}
      </Typography>
    )
  }
  return (
    <Box display={"flex"} flexDirection="row" justifyContent="space-around" flexWrap={"wrap"}>
      <Box
        style={{
          maxWidth: "400px",
          width: "100%",
        }}
      >
        <Pie data={data} options={options} />
      </Box>
      <Box>
        <Typography variant="body1" gutterBottom>
          {translations.managersLabel}
        </Typography>
        <Box sx={{ maxHeight: isSmallScreen ? "200px" : "400px", overflow: "scroll" }}>
          {legendData.map((item, index) => (
            <LegendItem
              key={index}
              label={item.label}
              color={item.boxColor}
              percentage={item.percentage}
              count={item.value}
              hidePercentage={true}
              patternType={item.pattern as "line" | "diagonal" | "square" | "zigzag"}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default ManagersBasedPieChart
type ManagersBasedPieChartProps = {
  totalNumberOfProjects: number
  groupedProjects: GroupedProjectsBasedOnResources
}
